type AnalyticsClient = {
  capture: (eventName: string, properties?: Record<string, unknown>) => unknown;
};

const PENDING_AUTH_EVENT_KEY = "querygen:pending-auth-analytics";
const PENDING_AUTH_EVENT_TTL_MS = 60 * 60 * 1000;
const MARKETING_HANDOFF_STORAGE_KEY = "querygen:marketing-handoff";
const MARKETING_HANDOFF_TTL_MS = 7 * 24 * 60 * 60 * 1000;
export const MARKETING_HANDOFF_HOST_PARAM = "qg_source_host";
export const MARKETING_HANDOFF_PATH_PARAM = "qg_source_path";
export const MARKETING_HANDOFF_ID_PARAM = "qg_handoff_id";

const AUTH_EVENT_PARAM = "qg_auth_event";
const AUTH_METHOD_PARAM = "qg_auth_method";
const AUTH_CONTEXT_PARAM = "qg_auth_context";
const AUTH_ATTEMPT_ID_PARAM = "qg_auth_attempt_id";
const AUTH_ATTEMPTED_AT_PARAM = "qg_auth_attempted_at";
const AUTH_EMAIL_VERIFICATION_PARAM = "qg_email_verification_required";
const AUTH_HANDOFF_ID_PARAM = "qg_auth_handoff_id";

type SignupSource = "purchase_intent" | "promotion" | "invitation" | "direct";
type LoginEntryContext = "purchase_intent" | "invitation" | "standard";
type OnboardingChannel = "whatsapp" | "whatsapp-business" | "instagram" | "skipped" | "unknown";
// existing_workspace: named elsewhere (e.g. WhatsApp signup), so the step was skipped
type WorkspaceEntry = "invitation" | "new_workspace" | "existing_workspace";

type AttributionProperties = {
  handoff_id?: string;
};

export type ProductAnalyticsEvent =
  | {
      name: "marketing_app_handoff_landed";
      properties: {
        source_host: "querygen.ai" | "www.querygen.ai";
        source_path: string;
        destination_path: string;
        attribution_type: "click_id" | "legacy_params" | "referrer";
      } & AttributionProperties;
    }
  | {
      name: "user_signed_up";
      properties: {
        auth_method: "email" | "google";
        signup_source: SignupSource;
        email_verification_required: boolean;
        auth_attempt_id?: string;
      } & AttributionProperties;
    }
  | {
      name: "user_logged_in";
      properties: {
        auth_method: "password" | "magic_link" | "email_code" | "phone_code" | "google";
        entry_context: LoginEntryContext;
        auth_attempt_id?: string;
      } & AttributionProperties;
    }
  | {
      name: "onboarding_step_completed";
      properties: {
        step: "workspace" | "plan" | "channel";
        workspace_entry: WorkspaceEntry;
        channel?: OnboardingChannel;
      } & AttributionProperties;
    }
  | {
      name: "onboarding_channel_selected";
      properties: {
        channel: Exclude<OnboardingChannel, "unknown">;
        workspace_entry: WorkspaceEntry;
      } & AttributionProperties;
    }
  | {
      name: "onboarding_completed";
      properties: {
        channel: OnboardingChannel;
        workspace_entry: WorkspaceEntry;
        plan_selected: boolean;
        promotion_applied: boolean;
      } & AttributionProperties;
    }
  | {
      name: "flow_created" | "flow_updated";
      properties: {
        surface: "automation" | "lead" | "task";
        trigger_type: string;
        action_type: string;
        setup_type: "custom" | "task-tracker" | "lead-tracker" | "payment-tracker";
        historical_processing_enabled: boolean;
      };
    };

type PendingAuthAnalyticsEvent = Extract<
  ProductAnalyticsEvent,
  { name: "user_signed_up" | "user_logged_in" }
>;

type StoredPendingAuthEvent = {
  createdAt: number;
  event: PendingAuthAnalyticsEvent;
};

type StoredMarketingHandoff = {
  createdAt: number;
  handoffId: string;
};

function getLocalStorage(): Storage | null {
  if (typeof window === "undefined") return null;

  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function rememberPendingAuthEvent(event: PendingAuthAnalyticsEvent): void {
  try {
    getLocalStorage()?.setItem(PENDING_AUTH_EVENT_KEY, JSON.stringify({
      createdAt: Date.now(),
      event,
    } satisfies StoredPendingAuthEvent));
  } catch {
    // Authentication must continue when browser storage is unavailable.
  }
}

export function clearPendingAuthEvent(): void {
  try {
    getLocalStorage()?.removeItem(PENDING_AUTH_EVENT_KEY);
  } catch {
    // Authentication must continue when browser storage is unavailable.
  }
}

export function takePendingAuthEvent(
  now = Date.now(),
): PendingAuthAnalyticsEvent | null {
  const storage = getLocalStorage();
  if (!storage) return null;

  try {
    const value = storage.getItem(PENDING_AUTH_EVENT_KEY);
    storage.removeItem(PENDING_AUTH_EVENT_KEY);
    if (!value) return null;

    const stored = JSON.parse(value) as Partial<StoredPendingAuthEvent>;
    if (
      typeof stored.createdAt !== "number" ||
      now - stored.createdAt > PENDING_AUTH_EVENT_TTL_MS ||
      !stored.event ||
      (stored.event.name !== "user_signed_up" && stored.event.name !== "user_logged_in")
    ) {
      return null;
    }

    return stored.event;
  } catch {
    return null;
  }
}

function isValidHandoffId(value: string | null): value is string {
  return Boolean(value && /^[a-zA-Z0-9_-]{8,100}$/.test(value));
}

function isValidAuthAttemptId(value: string | null): value is string {
  return Boolean(value && /^[a-zA-Z0-9_-]{8,100}$/.test(value));
}

export function rememberMarketingHandoff(handoffId: string): void {
  if (!isValidHandoffId(handoffId)) return;

  try {
    getLocalStorage()?.setItem(MARKETING_HANDOFF_STORAGE_KEY, JSON.stringify({
      createdAt: Date.now(),
      handoffId,
    } satisfies StoredMarketingHandoff));
  } catch {
    // Attribution must never block navigation or authentication.
  }
}

function getActiveMarketingHandoff(now = Date.now()): string | null {
  const storage = getLocalStorage();
  if (!storage) return null;

  try {
    const value = storage.getItem(MARKETING_HANDOFF_STORAGE_KEY);
    if (!value) return null;

    const stored = JSON.parse(value) as Partial<StoredMarketingHandoff>;
    if (
      typeof stored.createdAt !== "number" ||
      now - stored.createdAt > MARKETING_HANDOFF_TTL_MS ||
      !isValidHandoffId(stored.handoffId ?? null)
    ) {
      storage.removeItem(MARKETING_HANDOFF_STORAGE_KEY);
      return null;
    }

    return stored.handoffId;
  } catch {
    return null;
  }
}

function clearMarketingHandoff(): void {
  try {
    getLocalStorage()?.removeItem(MARKETING_HANDOFF_STORAGE_KEY);
  } catch {
    // Attribution cleanup is best effort.
  }
}

export function captureProductEvent(
  client: AnalyticsClient,
  event: ProductAnalyticsEvent,
): void {
  try {
    const shouldAttachStoredHandoff = event.name === "user_signed_up" ||
      event.name === "user_logged_in" ||
      event.name === "onboarding_step_completed" ||
      event.name === "onboarding_channel_selected" ||
      event.name === "onboarding_completed";
    const explicitHandoffId = "handoff_id" in event.properties
      ? event.properties.handoff_id
      : undefined;
    const handoffId = explicitHandoffId ?? (
      shouldAttachStoredHandoff ? getActiveMarketingHandoff() : null
    );
    const properties = handoffId
      ? { ...event.properties, handoff_id: handoffId }
      : event.properties;

    client.capture(event.name, properties);
    if (event.name === "onboarding_completed") {
      clearMarketingHandoff();
    }
  } catch (error) {
    // Analytics must never turn a completed product action into a reported failure.
    console.error("Failed to capture product analytics event:", error);
  }
}

export function captureOnboardingCompletion(
  client: AnalyticsClient,
  properties: Extract<ProductAnalyticsEvent, { name: "onboarding_completed" }>["properties"],
): void {
  captureProductEvent(client, {
    name: "onboarding_step_completed",
    properties: {
      step: "channel",
      channel: properties.channel,
      workspace_entry: properties.workspace_entry,
    },
  });
  captureProductEvent(client, { name: "onboarding_completed", properties });
}

function createAuthAttemptId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `auth-${Date.now()}`;
}

export function addAuthEventToCallbackUrl(
  callbackUrl: string,
  event: PendingAuthAnalyticsEvent,
  now = Date.now(),
  attemptId = createAuthAttemptId(),
): string {
  const url = new URL(callbackUrl);
  url.searchParams.set(AUTH_EVENT_PARAM, event.name);
  url.searchParams.set(AUTH_METHOD_PARAM, event.properties.auth_method);
  url.searchParams.set(AUTH_ATTEMPT_ID_PARAM, attemptId);
  url.searchParams.set(AUTH_ATTEMPTED_AT_PARAM, String(now));

  if (event.name === "user_logged_in") {
    url.searchParams.set(AUTH_CONTEXT_PARAM, event.properties.entry_context);
  } else {
    url.searchParams.set(AUTH_CONTEXT_PARAM, event.properties.signup_source);
    url.searchParams.set(
      AUTH_EMAIL_VERIFICATION_PARAM,
      String(event.properties.email_verification_required),
    );
  }

  const handoffId = event.properties.handoff_id ?? getActiveMarketingHandoff(now);
  if (handoffId) {
    url.searchParams.set(AUTH_HANDOFF_ID_PARAM, handoffId);
  }

  return url.toString();
}

export function getAuthEventFromCallbackUrl(
  currentUrl: string,
  now = Date.now(),
): PendingAuthAnalyticsEvent | null {
  try {
    const url = new URL(currentUrl);
    const name = url.searchParams.get(AUTH_EVENT_PARAM);
    const method = url.searchParams.get(AUTH_METHOD_PARAM);
    const context = url.searchParams.get(AUTH_CONTEXT_PARAM);
    const attemptId = url.searchParams.get(AUTH_ATTEMPT_ID_PARAM);
    const attemptedAt = Number(url.searchParams.get(AUTH_ATTEMPTED_AT_PARAM));
    const handoffId = url.searchParams.get(AUTH_HANDOFF_ID_PARAM);

    if (
      !isValidAuthAttemptId(attemptId) ||
      !Number.isFinite(attemptedAt) ||
      attemptedAt > now + 5 * 60 * 1000 ||
      now - attemptedAt > PENDING_AUTH_EVENT_TTL_MS
    ) {
      return null;
    }

    const attribution = isValidHandoffId(handoffId) ? { handoff_id: handoffId } : {};
    if (
      name === "user_logged_in" &&
      (method === "magic_link" || method === "google") &&
      (context === "purchase_intent" || context === "invitation" || context === "standard")
    ) {
      return {
        name,
        properties: {
          auth_method: method,
          entry_context: context,
          auth_attempt_id: attemptId,
          ...attribution,
        },
      };
    }

    if (
      name === "user_signed_up" &&
      method === "google" &&
      (context === "purchase_intent" || context === "promotion" || context === "invitation" || context === "direct")
    ) {
      return {
        name,
        properties: {
          auth_method: method,
          signup_source: context,
          email_verification_required:
            url.searchParams.get(AUTH_EMAIL_VERIFICATION_PARAM) === "true",
          auth_attempt_id: attemptId,
          ...attribution,
        },
      };
    }

    return null;
  } catch {
    return null;
  }
}

export function getUrlWithoutAuthEvent(currentUrl: string): string {
  const url = new URL(currentUrl);
  for (const param of [
    AUTH_EVENT_PARAM,
    AUTH_METHOD_PARAM,
    AUTH_CONTEXT_PARAM,
    AUTH_ATTEMPT_ID_PARAM,
    AUTH_ATTEMPTED_AT_PARAM,
    AUTH_EMAIL_VERIFICATION_PARAM,
    AUTH_HANDOFF_ID_PARAM,
  ]) {
    url.searchParams.delete(param);
  }
  return `${url.pathname}${url.search}${url.hash}`;
}

export function getMarketingAppHandoff(
  referrer: string,
  currentUrl: string,
): Extract<ProductAnalyticsEvent, { name: "marketing_app_handoff_landed" }> | null {
  try {
    const destination = new URL(currentUrl);
    const carriedHost = destination.searchParams.get(MARKETING_HANDOFF_HOST_PARAM);
    const carriedPath = destination.searchParams.get(MARKETING_HANDOFF_PATH_PARAM);
    const carriedHandoffId = destination.searchParams.get(MARKETING_HANDOFF_ID_PARAM);

    if (
      (carriedHost === "querygen.ai" || carriedHost === "www.querygen.ai") &&
      carriedPath?.startsWith("/") &&
      carriedPath.length <= 500
    ) {
      return {
        name: "marketing_app_handoff_landed",
        properties: {
          source_host: carriedHost,
          source_path: carriedPath,
          destination_path: destination.pathname,
          attribution_type: isValidHandoffId(carriedHandoffId)
            ? "click_id"
            : "legacy_params",
          ...(isValidHandoffId(carriedHandoffId)
            ? { handoff_id: carriedHandoffId }
            : {}),
        },
      };
    }

    if (!referrer) return null;
    const source = new URL(referrer);
    if (source.hostname !== "querygen.ai" && source.hostname !== "www.querygen.ai") return null;

    return {
      name: "marketing_app_handoff_landed",
      properties: {
        source_host: source.hostname,
        source_path: source.pathname,
        destination_path: destination.pathname,
        attribution_type: "referrer",
      },
    };
  } catch {
    return null;
  }
}

export function getUrlWithoutMarketingHandoff(currentUrl: string): string {
  const url = new URL(currentUrl);
  url.searchParams.delete(MARKETING_HANDOFF_HOST_PARAM);
  url.searchParams.delete(MARKETING_HANDOFF_PATH_PARAM);
  url.searchParams.delete(MARKETING_HANDOFF_ID_PARAM);
  return `${url.pathname}${url.search}${url.hash}`;
}
